// firebase.js
import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, memoryLocalCache, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyC5zfrPOU-tJDCERAhKtazRpsuLBGSFcb8",
  authDomain: "cpp-field-service-app.firebaseapp.com",
  projectId: "cpp-field-service-app",
  storageBucket: "cpp-field-service-app.firebasestorage.app",
  messagingSenderId: "717738748423",
  appId: "1:717738748423:web:6ac846c0f327f8f54658a2"
};

// A Master Admin Console "Impersonate" tab (App.js's ?impersonate= handling) can't just sign
// in on the same Firebase app instance every other tab uses — persistentMultipleTabManager
// below deliberately coordinates ONE shared IndexedDB cache across every tab of this origin,
// which assumes every tab is the SAME signed-in user. Confirmed live: signing in as a
// different user in a second tab under that shared app instance corrupted BOTH tabs (the
// platform admin's own separate tab got signed out, and the impersonated tab itself got
// permission-denied on its own reads) — regardless of which Firebase Auth persistence type
// was used, since Firestore's own offline cache is the thing that's actually shared, not just
// Auth. A second, distinctly-named Firebase app instance gets entirely separate storage
// (Firebase namespaces persisted state by app name), so an impersonation tab's session can
// never touch or coordinate with any other tab's — no other file needs to change, since every
// other file just imports these same `auth`/`db` names.
//
// The `typeof window` check exists so this module can also be imported from Node (the Layer 2
// integration tests run under jest's 'node' environment and import real service modules, which
// in turn import this file). In a browser `typeof window` is always 'object', so this is
// exactly the expression it was before for every real user; in Node it resolves to false
// instead of throwing on `window.location`.
const isImpersonationTab = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('impersonate');

const app = isImpersonationTab
  ? initializeApp(firebaseConfig, 'impersonation')
  : initializeApp(firebaseConfig);

// Initialize Firebase services

// Persistent local cache (IndexedDB) lets technicians see their last-synced work orders and
// schedule while offline in the field, and queues writes made offline (e.g. tapping "I've
// Arrived" in a dead zone) to sync automatically once back online. Multi-tab manager avoids
// persistence errors if an admin has this app open in more than one browser tab at once — not
// used for an impersonation tab (memory-only cache instead), since there's no "multiple tabs
// of the same session" to coordinate for a one-off, single-tab impersonated session.
//
// IndexedDB does not exist in Node either, so the integration tests get the memory cache — the
// same one an impersonation tab already uses, so this is an existing, exercised code path
// rather than a new one invented for tests.
const useMemoryCache = isImpersonationTab || typeof indexedDB === 'undefined';

export const auth = getAuth(app);
export const db = useMemoryCache
  ? initializeFirestore(app, { localCache: memoryLocalCache() })
  : initializeFirestore(app, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });
export const storage = getStorage(app);

// Emulator wiring — only active when REACT_APP_USE_EMULATOR is set, which happens exclusively
// in the integration test harness (see package.json's test:integration script). Production and
// dev builds never set it.
//
// This is inert in a production build in the strongest sense available: Create React App
// substitutes `process.env.REACT_APP_USE_EMULATOR` with a literal at BUILD time, so with the
// variable unset the condition compiles to `undefined === 'true'` and Terser removes the whole
// branch — the connect* calls and the 127.0.0.1 addresses are not merely unreachable at
// runtime, they are absent from the shipped bundle entirely. Verified by grepping build output.
if (process.env.REACT_APP_USE_EMULATOR === 'true') {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

export default app;
